
import React, { useEffect, useRef, useState } from 'react';
import { toast } from "@/utils/toast";
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader, Users, Search, VideoOff, MicOff, Camera, RefreshCw } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useChat } from '@/contexts/ChatContext';
import LoadingState from './LoadingState';

interface VideoChatProps {
  isActive?: boolean;
  isConnected?: boolean;
  quality?: 'auto' | 'low' | 'medium' | 'high';
  className?: string;
}

const VideoChat: React.FC<VideoChatProps> = ({ 
  isActive = true, 
  isConnected = false,
  quality = 'auto', 
  className 
}) => {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const isMobile = useIsMobile();
  const [localVideoMuted, setLocalVideoMuted] = useState(false);
  const [localAudioMuted, setLocalAudioMuted] = useState(false);
  const [remoteVideoAvailable, setRemoteVideoAvailable] = useState(false);
  const [showCameraPrompt, setShowCameraPrompt] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const previousRemoteStreamRef = useRef<MediaStream | null>(null);
  const streamChangeTimeoutRef = useRef<number | null>(null);
  const playAttemptCountRef = useRef(0);
  const localStreamRetriesRef = useRef(0);
  
  const { 
    localStream, 
    remoteStream, 
    connectionStatus, 
    isMicrophoneEnabled, 
    isVideoEnabled,
    isSearchingForPartner,
    onlineUsers,
    toggleMicrophone,
    toggleVideo
  } = useChat();
  
  useEffect(() => {
    if (remoteStream !== previousRemoteStreamRef.current) {
      setIsTransitioning(true);
      console.log("Remote stream changing - transition started");
      
      if (streamChangeTimeoutRef.current) {
        window.clearTimeout(streamChangeTimeoutRef.current);
      }
      
      streamChangeTimeoutRef.current = window.setTimeout(() => {
        console.log("Transition complete");
        setIsTransitioning(false);
        previousRemoteStreamRef.current = remoteStream;
      }, 300);
    }
    
    return () => {
      if (streamChangeTimeoutRef.current) {
        window.clearTimeout(streamChangeTimeoutRef.current);
      }
    };
  }, [remoteStream]);
  
  useEffect(() => {
    let mounted = true;
    playAttemptCountRef.current = 0;
    
    const setupLocalVideo = () => {
      if (!localVideoRef.current || !mounted) return;
      
      if (localStream) {
        console.log("Setting up local video stream");
        localVideoRef.current.srcObject = localStream;
        
        const playVideo = () => {
          if (!localVideoRef.current || !mounted) return;
          
          playAttemptCountRef.current++;
          console.log(`Attempt #${playAttemptCountRef.current} to play local video`);
          
          const playPromise = localVideoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.log("Error playing local video:", error);
              
              if (playAttemptCountRef.current < 3 && mounted) {
                console.log(`Retrying local video play, attempt #${playAttemptCountRef.current + 1}`);
                setTimeout(playVideo, 200);
              } else if (mounted) {
                setShowCameraPrompt(true);
              }
            });
          }
        };
        
        playVideo();
        
        const videoTracks = localStream.getVideoTracks();
        const hasVideoTracks = videoTracks.length > 0;
        const isVideoActive = hasVideoTracks && videoTracks[0].enabled;
        
        setLocalVideoMuted(!isVideoActive);
        setShowCameraPrompt(!hasVideoTracks);
        
        if (hasVideoTracks) {
          console.log(`Local video track active: ${isVideoActive}, track ID: ${videoTracks[0].id}`);
        } else {
          console.warn("No video tracks found in local stream");
        }
      } else {
        console.warn("No local stream available");
        setShowCameraPrompt(true);
        setLocalVideoMuted(true);
      }
    };
    
    if (localStream) {
      localStreamRetriesRef.current = 0;
      setupLocalVideo();
    } else if (localStreamRetriesRef.current < 3) {
      localStreamRetriesRef.current++;
      console.log(`Local stream not available, retry ${localStreamRetriesRef.current}/3 in 500ms`);
      setTimeout(setupLocalVideo, 500);
    }
    
    return () => {
      mounted = false;
    };
  }, [localStream, isVideoEnabled]);
  
  useEffect(() => {
    setLocalVideoMuted(!isVideoEnabled);
    setLocalAudioMuted(!isMicrophoneEnabled);
  }, [isVideoEnabled, isMicrophoneEnabled]);
  
  useEffect(() => {
    if (!isTransitioning) {
      const setupRemoteVideo = () => {
        if (!remoteVideoRef.current || !remoteStream) return;
        
        console.log("Setting up remote video stream");
        remoteVideoRef.current.srcObject = remoteStream;
        
        const hasVideoTracks = remoteStream.getVideoTracks().length > 0;
        setRemoteVideoAvailable(hasVideoTracks);
        
        const playVideo = () => {
          if (!remoteVideoRef.current) return;
          
          const playPromise = remoteVideoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.error("Error playing remote video:", error);
            });
          }
        };
        
        playVideo();
      };
      
      setupRemoteVideo();
    }
    
    return () => {};
  }, [remoteStream, isTransitioning]);
  
  useEffect(() => {
    if (localStream) {
      const videoTracks = localStream.getVideoTracks();
      if (videoTracks.length > 0) {
        const videoTrack = videoTracks[0];
        
        let constraints: MediaTrackConstraints = {};
        
        switch (quality) {
          case 'low':
            constraints = {
              width: { ideal: 640 },
              height: { ideal: 480 }
            };
            break;
          case 'medium':
            constraints = {
              width: { ideal: 1280 },
              height: { ideal: 720 }
            };
            break;
          case 'high':
            constraints = {
              width: { ideal: 1920 },
              height: { ideal: 1080 }
            };
            break;
          case 'auto':
          default:
            break;
        }
        
        if (Object.keys(constraints).length > 0) {
          videoTrack.applyConstraints(constraints)
            .catch(error => {
              console.error("Error applying video constraints:", error);
            });
        }
      }
    }
  }, [localStream, quality]);
  
  const handleRetryCamera = async () => {
    try {
      setShowCameraPrompt(false);
      
      if (localStream) {
        toggleVideo(true);
        
        setTimeout(() => {
          if (!isVideoEnabled) {
            toast.error("Could not enable camera");
            setShowCameraPrompt(true);
          } else {
            toast.success("Camera enabled successfully");
          }
        }, 200);
      } else {
        toast.warning("No camera stream available. Trying to reconnect...");
        
        toggleVideo(false);
        setTimeout(() => toggleVideo(true), 200);
        
        setShowCameraPrompt(true);
      }
    } catch (error) {
      console.error("Error retrying camera:", error);
      toast.error("Failed to enable camera");
      setShowCameraPrompt(true);
    }
  };
  
  return (
    <div className={cn(
      "relative w-full h-full overflow-hidden touch-action-manipulation",
      "bg-[#10121f]",
      className
    )}>
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className={cn(
          "absolute z-10 overflow-hidden hardware-accelerated",
          "shadow-xl shadow-black/30 backdrop-blur-sm",
          "border-2 border-white/10",
          isMobile ? 
            "bottom-28 right-4 w-32 h-32 rounded-full" : 
            "bottom-6 right-6 w-44 h-44 rounded-full"
        )}
      >
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          className={cn(
            "w-full h-full object-cover transition-all duration-300 hardware-accelerated will-change-transform",
            "hover:scale-105",
            !localVideoMuted && localStream ? "opacity-100" : "opacity-0"
          )}
        />
        
        <AnimatePresence>
          {localVideoMuted && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 flex items-center justify-center bg-[#10121f] rounded-full text-white"
            >
              <div className="relative w-12 h-12 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-gray-800 animate-pulse opacity-30"></div>
                <VideoOff size={isMobile ? 32 : 36} className="relative z-10" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        <AnimatePresence>
          {showCameraPrompt && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 flex flex-col items-center justify-center bg-[#10121f] rounded-full text-white"
            >
              <Camera size={isMobile ? 28 : 32} className="mb-1" />
              <button 
                onClick={handleRetryCamera}
                className="flex items-center justify-center bg-blue-700/50 hover:bg-blue-700/70 p-2 rounded-full mt-2 transition-colors"
              >
                <RefreshCw size={isMobile ? 16 : 20} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        
        <AnimatePresence>
          {!localStream && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 flex items-center justify-center bg-[#10121f]/80 rounded-full text-xs text-white"
            >
              <LoadingState text="Starting camera..." size="small" variant="dots" />
            </motion.div>
          )}
        </AnimatePresence>
        
        <AnimatePresence>
          {localAudioMuted && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ duration: 0.2 }}
              className="absolute bottom-2 right-2 w-8 h-8 bg-red-500/80 rounded-full flex items-center justify-center"
            >
              <MicOff size={16} className="text-white" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      
      <div className="w-full h-full overflow-hidden relative">
        <AnimatePresence mode="wait">
          {remoteStream && remoteVideoAvailable && !isTransitioning && (
            <motion.div
              key="remote-video"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 w-full h-full"
            >
              <div className="absolute inset-0 bg-gradient-to-t from-[#10121f]/30 to-transparent z-0 pointer-events-none"></div>
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover hardware-accelerated will-change-transform"
              />
            </motion.div>
          )}
          
          {remoteStream && !remoteVideoAvailable && connectionStatus === 'connected' && !isTransitioning && (
            <motion.div
              key="video-muted"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 flex flex-col items-center justify-center bg-[#10121f] backdrop-blur-sm"
            >
              <div className="w-24 h-24 rounded-full bg-gray-800/50 flex items-center justify-center">
                <VideoOff size={48} className="text-white/70" />
              </div>
              <p className="text-white/70 text-lg mt-4">Camera is off</p>
            </motion.div>
          )}
          
          {(isSearchingForPartner || connectionStatus !== 'connected' || isTransitioning) && (
            <motion.div
              key="initializing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 flex flex-col items-center justify-center bg-[#10121f] backdrop-blur-sm"
            >
              <p className="text-white/80 text-lg mb-6">
                {isSearchingForPartner ? "Finding someone to chat with..." : 
                 isTransitioning ? "Connecting to new partner..." : "Initializing video..."}
              </p>
              <div className="w-10 h-10 border-3 border-t-transparent border-white/40 rounded-full animate-spin"></div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default VideoChat;
